
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { v4 as uuidv4 } from 'uuid';

export type PhaseStatus = 'pending' | 'in-progress' | 'completed' | 'error';

interface PhaseUpdateParams {
  formId: string;
  phaseNumber: number;
  status: PhaseStatus;
}

export const useOnboardingPhases = () => {
  const [isUpdating, setIsUpdating] = useState(false);

  const updatePhaseStatus = useCallback(async ({
    formId,
    phaseNumber,
    status
  }: PhaseUpdateParams): Promise<boolean> => {
    if (!formId || phaseNumber < 1 || phaseNumber > 7) {
      console.error('Invalid parameters for phase update');
      return false;
    }

    setIsUpdating(true);
    try {
      // Using Record to properly type the update data object
      const updateData: Record<string, PhaseStatus | string> = {
        [`status_phase_${phaseNumber}`]: status
      };

      // For phase 1 completion, automatically set phase 2 to in-progress
      if (phaseNumber === 1 && status === 'completed') {
        updateData[`status_phase_2`] = 'in-progress';
      }

      // When approving (phase 2 completion), automatically set phase 3 to in-progress
      if (phaseNumber === 2 && status === 'completed') {
        updateData[`status_phase_3`] = 'in-progress';
        // Also update the approved status for backward compatibility
        // This is a separate field with its own type, not a PhaseStatus
        updateData['approved'] = 'approved';
      }

      const { error } = await supabase
        .from('onboarding_forms')
        .update(updateData)
        .eq('id', formId);

      if (error) {
        console.error('Error updating phase status:', error);
        toast({
          title: "Update Failed",
          description: "Could not update the phase status",
          variant: "destructive"
        });
        return false;
      }

      toast({
        title: "Status Updated",
        description: `Phase ${phaseNumber} is now ${status}`,
      });
      
      return true;
    } catch (error) {
      console.error('Error in updatePhaseStatus:', error);
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const generateOnboardingLink = async (formId: string): Promise<string | null> => {
    try {
      setIsUpdating(true);

      // Generate a unique identifier for this link
      const uniqueId = uuidv4();
      const baseUrl = window.location.origin;
      const uniqueLink = `${baseUrl}/onboarding/${uniqueId}`;

      // First, create an entry in the onboarding_links table
      const { error: linkError } = await supabase
        .from('onboarding_links')
        .insert({
          form_id: formId,
          unique_link: uniqueLink,
          status: 'active'
        });

      if (linkError) {
        throw linkError;
      }

      return uniqueLink;
    } catch (error) {
      console.error('Error generating onboarding link:', error);
      toast({
        title: "Error",
        description: "Failed to generate onboarding link",
        variant: "destructive"
      });
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Determines the current phase number based on phase statuses
   */
  const getCurrentPhase = (formData: any): number => {
    if (!formData) return 1;
    
    if (formData.status_phase_7 === "completed") return 7;
    if (formData.status_phase_6 === "completed" || formData.status_phase_6 === "in-progress") return 6;
    if (formData.status_phase_5 === "completed" || formData.status_phase_5 === "in-progress") return 5;
    if (formData.status_phase_4 === "completed" || formData.status_phase_4 === "in-progress") return 4;
    if (formData.status_phase_3 === "completed" || formData.status_phase_3 === "in-progress") return 3;
    if (formData.status_phase_2 === "completed" || formData.status_phase_2 === "in-progress") return 2;
    return 1;
  };

  return {
    updatePhaseStatus,
    generateOnboardingLink,
    getCurrentPhase,
    isUpdating
  };
};
